import { AnimatePresence, motion } from 'framer-motion';
import jsPDF from 'jspdf';
import { Calendar, Download, Search } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import userService from '../../services/userService';
import { fdocApi } from '../Tax/FdocApi';
import { Card } from '../ui';
import { billApi } from './billApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Map API bill response to UI format
 */
const mapBillToUI = (apiBill) => {
  const billDate = apiBill.bill_timestamp 
    ? new Date(apiBill.bill_timestamp).toISOString().split('T')[0]
    : apiBill.created_at 
      ? new Date(apiBill.created_at).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];

  return {
    id: apiBill.bill_id,
    billId: apiBill.bill_id,
    orderNumber: apiBill.order_id || '',
    billNumber: apiBill.bill_number || '',
    date: billDate,
    tableNumber: apiBill.table_number || '',
    siteId: apiBill.site_id || '',
    siteName: apiBill.site_name || '',
    customerName: apiBill.customer?.name || '',
    customerMobile: apiBill.customer?.mobile || '',
    customerEmail: apiBill.customer?.email || '',
    customerId: apiBill.customer_id || null,
    items: [], // Will be fetched when detail modal opens
    subtotal: apiBill.subtotal || 0,
    discountCode: apiBill.discount_code || '',
    discountValue: apiBill.discount_amount || 0,
    discountedSubtotal: (apiBill.subtotal || 0) - (apiBill.discount_amount || 0),
    cgstRate: apiBill.cgst && apiBill.subtotal ? ((apiBill.cgst / (apiBill.subtotal - (apiBill.discount_amount || 0))) * 100).toFixed(1) : 0,
    sgstRate: apiBill.sgst && apiBill.subtotal ? ((apiBill.sgst / (apiBill.subtotal - (apiBill.discount_amount || 0))) * 100).toFixed(1) : 0,
    cgstAmount: apiBill.cgst || 0,
    sgstAmount: apiBill.sgst || 0,
    grandTotal: apiBill.total || 0,
    lineItemsCount: apiBill.line_items_count || 0,
    version_no: apiBill.version_no ?? apiBill.versionNo ?? null,
    versionNo: apiBill.version_no ?? apiBill.versionNo ?? null,
    // Store full API bill for detail modal
    apiBill: apiBill,
  };
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

const BillCustomerView = ({ 
  externalSite = null, 
  externalMonth = null,
  hideSelectors = false,
  fetchFromFdoc = false,
  esignedOnly = false,
}) => {
  // Log screen view
  useScreenLogging('BillCustomerView', {
    component: 'BillCustomerView',
    screenType: 'bill-customer-view',
  });

  // Store fetchFromFdoc in a ref so it's accessible in callbacks
  const fetchFromFdocRef = useRef(fetchFromFdoc);
  useEffect(() => {
    fetchFromFdocRef.current = fetchFromFdoc;
  }, [fetchFromFdoc]);

  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(externalMonth || getCurrentMonth());
  const [selectedCard, setSelectedCard] = useState(null);
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [fullBillDetails, setFullBillDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadUrlExpiresIn, setDownloadUrlExpiresIn] = useState(null);
  const [documentContentType, setDocumentContentType] = useState(null);
  const [billFileStatus, setBillFileStatus] = useState(null);

  // Update internal state when external props change
  useEffect(() => {
    if (externalMonth !== null) {
      setSelectedMonth(externalMonth);
    }
  }, [externalMonth]);

  // Calculate date range for selected month
  const getDateRange = useMemo(() => {
    const [year, month] = selectedMonth.split('-');
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    
    // Create start date (first day of month at 00:00:00 UTC)
    const startDate = new Date(Date.UTC(yearNum, monthNum - 1, 1, 0, 0, 0));
    // Create end date (last day of month at 23:59:59 UTC)
    const endDate = new Date(Date.UTC(yearNum, monthNum, 0, 23, 59, 59));
    
    // Format as YYYY-MM-DD (for date-only filtering)
    // Backend should handle date range inclusively
    const formatDate = (date) => {
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    return {
      start: formatDate(startDate),
      end: formatDate(endDate),
    };
  }, [selectedMonth]);

  // Fetch bills from API
  const fetchBills = useCallback(async (reset = false, currentLastKey = null) => {
    setIsLoading(true);
    setError('');

    try {
      // If fetchFromFdoc is true, fetch from FDOC API instead of bill API
      if (fetchFromFdoc) {
        console.log('BillCustomerView: Fetching bills from FDOC (Bill OO)');
        const fdocParams = { limit: 500 };
        if (esignedOnly) {
          fdocParams.esign_status = 'applied';
        }
        const fdocResponse = await fdocApi.listDocuments(fdocParams);
        
        // Process FDOC documents to find Bill types
        const fdocBatchSize = 10;
        const fdocBills = [];
        const siteId = externalSite ? resolveSiteValue(externalSite) : null;
        const startDate = getDateRange.start;
        const endDate = getDateRange.end;
        
        for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
          const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
          const batchResults = await Promise.allSettled(
            batch.map(async (doc) => {
              try {
                let fullDoc;
                try {
                  fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                } catch (fetchErr) {
                  // Handle 500 errors and other fetch failures gracefully
                  const errorMessage = fetchErr.message || '';
                  const isServerError = errorMessage.includes('500') || 
                                       errorMessage.includes('Internal server error') ||
                                       errorMessage.includes('Server error') ||
                                       fetchErr.status === 500;
                  
                  if (isServerError) {
                    // Silently skip documents that return server errors - these are backend issues
                    // Don't log to avoid noise, just skip the document
                    return null;
                  }
                  // For other errors (404, 401, etc.), log and skip
                  console.warn(`BillCustomerView: Document ${doc.fdoc_id} error:`, errorMessage);
                  return null;
                }
                const extractedData = fullDoc.extracted_data;
                let docType = extractedData?.type || 'UNKNOWN';
                
                // Infer type from file name if needed
                if (docType === 'UNKNOWN' || !docType) {
                  const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                  const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                  const combinedText = `${fileName} ${fileKey}`;
                  
                  if (combinedText.includes('bill') || combinedText.includes('invoice')) {
                    docType = 'Bill';
                  }
                }
                
                // Normalize Bill types
                const normalizedType = docType.toLowerCase();
                if (normalizedType === 'bill' || normalizedType === 'bills') {
                  // Get site_id from document
                  const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                  
                  // Check site_id match if site filter is applied
                  if (siteId) {
                    if (!docSiteId || docSiteId !== siteId) {
                      return null; // Skip if site doesn't match
                    }
                  }
                  
                  // Check date range if provided
                  const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                  if (docDate) {
                    if (startDate && docDate < startDate) {
                      return null;
                    }
                    if (endDate && docDate > endDate) {
                      return null;
                    }
                  }
                  
                  // Transform FDOC document to bill format
                  const extractedFields = extractedData?.fields || {};
                  const textBlocks = extractedFields.text_blocks || [];
                  const rawText = extractedFields.raw_text || '';
                  
                  // Extract bill number
                  let billNumber = extractedFields.bill_number || extractedFields.billNumber || extractedFields.invoice_number;
                  if (!billNumber && textBlocks.length > 0) {
                    const billMatch = textBlocks.find(block => 
                      typeof block === 'string' && /bill|invoice/i.test(block) && /\d+/.test(block)
                    );
                    if (billMatch) {
                      const numMatch = billMatch.match(/(\d+)/);
                      if (numMatch) {
                        billNumber = `BILL-${numMatch[1]}`;
                      }
                    }
                  }
                  if (!billNumber) {
                    billNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                  }
                  
                  // Extract order number
                  let orderNumber = extractedFields.order_id || extractedFields.order_number || extractedFields.orderNumber || '';
                  if (!orderNumber && textBlocks.length > 0) {
                    const orderIndex = textBlocks.findIndex(block => 
                      typeof block === 'string' && /order/i.test(block)
                    );
                    if (orderIndex >= 0 && orderIndex + 1 < textBlocks.length) {
                      const orderValue = textBlocks[orderIndex + 1];
                      if (typeof orderValue === 'string' && orderValue.trim().length > 0) {
                        // Skip if it looks like a UUID or date
                        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orderValue.trim()) &&
                            !/^\d{1,2}\s+\w+\s+\d{4}$/i.test(orderValue.trim())) {
                          orderNumber = orderValue.trim();
                        }
                      }
                    }
                  }
                  
                  // Filter by order ID if provided
                  if (orderIdFilter.trim() && orderNumber && !orderNumber.toLowerCase().includes(orderIdFilter.toLowerCase())) {
                    return null;
                  }
                  
                  // Extract table number
                  let tableNumber = extractedFields.table_number || extractedFields.tableNumber || extractedFields.table || '';
                  if (!tableNumber && textBlocks.length > 0) {
                    const tableIndex = textBlocks.findIndex(block => 
                      typeof block === 'string' && /table/i.test(block)
                    );
                    if (tableIndex >= 0 && tableIndex + 1 < textBlocks.length) {
                      const tableValue = textBlocks[tableIndex + 1];
                      if (typeof tableValue === 'string' && tableValue.trim().length > 0) {
                        tableNumber = tableValue.trim();
                      }
                    }
                  }
                  
                  // Extract customer information
                  let customerName = extractedFields.customer_name || extractedFields.customerName || extractedFields.client_name || '';
                  let customerMobile = extractedFields.customer_mobile || extractedFields.customerMobile || extractedFields.mobile || '';
                  let customerEmail = extractedFields.customer_email || extractedFields.customerEmail || extractedFields.email || '';
                  
                  if (textBlocks.length > 0) {
                    // Extract customer name
                    if (!customerName) {
                      const customerIndex = textBlocks.findIndex(block => 
                        typeof block === 'string' && /customer|client|name/i.test(block)
                      );
                      if (customerIndex >= 0 && customerIndex + 1 < textBlocks.length) {
                        const nameValue = textBlocks[customerIndex + 1];
                        if (typeof nameValue === 'string' && nameValue.trim().length > 0) {
                          if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(nameValue.trim()) &&
                              !/^\d{1,2}\s+\w+\s+\d{4}$/i.test(nameValue.trim()) &&
                              !nameValue.includes('@')) {
                            customerName = nameValue.trim();
                          }
                        }
                      }
                    }
                    
                    // Extract mobile number
                    if (!customerMobile) {
                      const mobileIndex = textBlocks.findIndex(block => 
                        typeof block === 'string' && /mobile|phone|contact/i.test(block)
                      );
                      if (mobileIndex >= 0 && mobileIndex + 1 < textBlocks.length) {
                        const mobileValue = textBlocks[mobileIndex + 1];
                        if (typeof mobileValue === 'string' && /^\d{10}$/.test(mobileValue.replace(/[\s-]/g, ''))) {
                          customerMobile = mobileValue.trim();
                        }
                      }
                    }
                    
                    // Extract email
                    if (!customerEmail) {
                      const emailIndex = textBlocks.findIndex(block => 
                        typeof block === 'string' && /@/.test(block)
                      );
                      if (emailIndex >= 0) {
                        customerEmail = textBlocks[emailIndex].trim();
                      }
                    }
                  }
                  
                  // Extract bill date
                  let billDate = extractedFields.bill_date || extractedFields.date || extractedFields.bill_timestamp;
                  if (!billDate && textBlocks.length > 0) {
                    const dateMatch = textBlocks.find(block => 
                      typeof block === 'string' && /date.*\d{1,2}\s+\w+\s+\d{4}/i.test(block)
                    );
                    if (dateMatch) {
                      const dateMatchResult = dateMatch.match(/(\d{1,2}\s+\w+\s+\d{4})/i);
                      if (dateMatchResult) {
                        try {
                          billDate = new Date(dateMatchResult[1]).toISOString().split('T')[0];
                        } catch (e) {
                          // If date parsing fails, use docDate
                        }
                      }
                    }
                  }
                  if (!billDate) {
                    billDate = docDate || new Date().toISOString().split('T')[0];
                  }
                  
                  // Extract amounts
                  let subtotal = parseFloat(extractedFields.subtotal || extractedFields.sub_total || 0);
                  let discountAmount = parseFloat(extractedFields.discount || extractedFields.discount_amount || 0);
                  let cgst = parseFloat(extractedFields.cgst || 0);
                  let sgst = parseFloat(extractedFields.sgst || 0);
                  let grandTotal = parseFloat(extractedFields.grand_total || extractedFields.total_amount || extractedFields.total || 0);
                  
                  // Extract from text_blocks if amounts are 0
                  if (textBlocks.length > 0 && (grandTotal === 0 || subtotal === 0)) {
                    // Extract grand total
                    const grandTotalIndex = textBlocks.findIndex(block => 
                      typeof block === 'string' && /grand\s+total|total/i.test(block)
                    );
                    if (grandTotalIndex >= 0) {
                      for (let offset = 1; offset <= 3 && grandTotalIndex + offset < textBlocks.length; offset++) {
                        const totalValue = textBlocks[grandTotalIndex + offset];
                        if (typeof totalValue === 'string') {
                          const cleanedTotal = totalValue.replace(/[₹,\s]/g, '');
                          const parsedTotal = parseFloat(cleanedTotal);
                          if (!isNaN(parsedTotal) && parsedTotal > 0) {
                            grandTotal = parsedTotal;
                            break;
                          }
                        }
                      }
                    }
                    
                    // Extract subtotal
                    const subtotalIndex = textBlocks.findIndex(block => 
                      typeof block === 'string' && /sub\s*total/i.test(block)
                    );
                    if (subtotalIndex >= 0 && subtotalIndex + 1 < textBlocks.length && subtotal === 0) {
                      const subtotalValue = textBlocks[subtotalIndex + 1];
                      if (typeof subtotalValue === 'string') {
                        const cleanedSubtotal = subtotalValue.replace(/[₹,\s]/g, '');
                        const parsedSubtotal = parseFloat(cleanedSubtotal);
                        if (!isNaN(parsedSubtotal) && parsedSubtotal > 0) {
                          subtotal = parsedSubtotal;
                        }
                      }
                    }
                  }
                  
                  // Extract line items from text_blocks
                  let lineItems = extractedFields.line_items || extractedFields.items || [];
                  
                  // If no line items, try to extract from text_blocks table format
                  if (lineItems.length === 0 && textBlocks.length > 0) {
                    // Look for table header row
                    let headerStartIndex = -1;
                    for (let i = 0; i < textBlocks.length - 6; i++) {
                      const blocks = textBlocks.slice(i, i + 7);
                      const blockStrings = blocks.filter(b => typeof b === 'string').map(b => b.toUpperCase());
                      
                      let matches = 0;
                      if (blockStrings[0] && (blockStrings[0].includes('S.NO') || blockStrings[0].includes('SNO'))) matches++;
                      if (blockStrings[1] && blockStrings[1].includes('NAME')) matches++;
                      if (blockStrings[2] && (blockStrings[2].includes('HSN') || blockStrings[2].includes('CODE'))) matches++;
                      if (blockStrings[3] && blockStrings[3].includes('UNITS')) matches++;
                      if (blockStrings[4] && blockStrings[4].includes('RATE')) matches++;
                      if (blockStrings[5] && blockStrings[5].includes('TAX')) matches++;
                      if (blockStrings[6] && blockStrings[6].includes('AMOUNT')) matches++;
                      
                      if (matches >= 5) {
                        headerStartIndex = i;
                        break;
                      }
                    }
                    
                    if (headerStartIndex >= 0) {
                      let itemStartIndex = headerStartIndex + 7;
                      for (let i = headerStartIndex + 7; i < textBlocks.length; i++) {
                        const block = textBlocks[i];
                        if (typeof block === 'string' && /^\d+$/.test(block.trim())) {
                          itemStartIndex = i;
                          break;
                        }
                      }
                      
                      let currentIndex = itemStartIndex;
                      while (currentIndex < textBlocks.length) {
                        const currentBlock = textBlocks[currentIndex];
                        if (typeof currentBlock === 'string') {
                          const upperBlock = currentBlock.toUpperCase();
                          if (upperBlock.includes('SUBTOTAL') || upperBlock.includes('DISCOUNT') || upperBlock.includes('GRAND TOTAL') || upperBlock.includes('PAGE')) {
                            break;
                          }
                        }
                        
                        if (currentIndex + 6 < textBlocks.length) {
                          const sno = textBlocks[currentIndex];
                          const name = textBlocks[currentIndex + 1];
                          const hsn = textBlocks[currentIndex + 2];
                          const units = textBlocks[currentIndex + 3];
                          const rate = textBlocks[currentIndex + 4];
                          const tax = textBlocks[currentIndex + 5];
                          const amount = textBlocks[currentIndex + 6];
                          
                          if (typeof sno === 'string' && /^\d+$/.test(sno.trim()) &&
                              typeof name === 'string' && name.trim().length > 0 &&
                              typeof units === 'string' && /^\d+(?:\.\d+)?$/.test(units.trim())) {
                            
                            const quantity = parseFloat(units) || 0;
                            const unitPrice = parseFloat(rate) || 0;
                            const amountStr = typeof amount === 'string' ? amount.replace(/[₹,\s]/g, '') : '0';
                            const lineTotal = parseFloat(amountStr) || 0;
                            
                            let taxRate = 0;
                            if (typeof tax === 'string') {
                              const taxMatch = tax.match(/(\d+(?:\.\d+)?)/);
                              if (taxMatch) {
                                taxRate = parseFloat(taxMatch[1]) || 0;
                              }
                            }
                            
                            lineItems.push({
                              item_name: name.trim(),
                              description: name.trim(),
                              quantity: quantity,
                              unit: 'pcs',
                              unit_price: unitPrice,
                              rate: unitPrice,
                              line_total: lineTotal,
                              tax_rate: taxRate,
                              hsn_code: typeof hsn === 'string' ? hsn.trim() : '',
                            });
                            
                            currentIndex += 7;
                          } else {
                            currentIndex++;
                          }
                        } else {
                          break;
                        }
                      }
                    }
                  }
                  
                  // Calculate subtotal from line items if not set
                  if (subtotal === 0 && lineItems.length > 0) {
                    subtotal = lineItems.reduce((sum, item) => {
                      return sum + (parseFloat(item.line_total) || parseFloat(item.amount) || 0);
                    }, 0);
                  }
                  
                  return {
                    bill_id: doc.fdoc_id,
                    bill_number: billNumber,
                    order_id: orderNumber,
                    bill_timestamp: billDate,
                    created_at: billDate,
                    table_number: tableNumber,
                    site_id: docSiteId,
                    site_name: fullDoc?.site_name || 'N/A',
                    customer: {
                      name: customerName || 'Unknown Customer',
                      mobile: customerMobile,
                      email: customerEmail,
                    },
                    customer_id: null,
                    line_items: lineItems,
                    items: lineItems,
                    subtotal: subtotal,
                    discount_code: extractedFields.discount_code || '',
                    discount_amount: discountAmount,
                    cgst: cgst,
                    sgst: sgst,
                    total: grandTotal,
                    line_items_count: lineItems.length,
                    version_no:
                      fullDoc?.version_no ??
                      fullDoc?.versionNo ??
                      doc?.version_no ??
                      doc?.versionNo ??
                      null,
                    // Store FDOC reference
                    fdoc_id: doc.fdoc_id,
                    fdoc_document: fullDoc,
                  };
                }
                return null;
                } catch (err) {
                // Only log non-500 errors to reduce noise
                const errorMessage = err.message || '';
                const isServerError = errorMessage.includes('500') || 
                                     errorMessage.includes('Internal server error') ||
                                     errorMessage.includes('Server error') ||
                                     err.status === 500;
                
                if (!isServerError) {
                  console.warn(`BillCustomerView: Failed to process FDOC document ${doc.fdoc_id}:`, errorMessage);
                }
                // Silently skip server errors - these are backend issues
                return null;
              }
            })
          );
          
          batchResults.forEach((result) => {
            if (result.status === 'fulfilled' && result.value) {
              fdocBills.push(result.value);
            }
          });
        }
        
        // Map FDOC bills to UI format
        const mappedBills = fdocBills.map(bill => mapBillToUI(bill));
        
        if (reset) {
          setBills(mappedBills);
        } else {
          setBills(prev => [...prev, ...mappedBills]);
        }
        
        setLastKey(null);
        setHasMore(false);
        return;
      }

      // Regular bill API fetch
      const params = {
        limit: 20,
        start_date: getDateRange.start,
        end_date: getDateRange.end,
      };

      // Add site_id filter if external site is provided
      if (externalSite) {
        const siteId = resolveSiteValue(externalSite);
        if (siteId) {
          params.site_id = siteId;
        }
      }

      console.log('BillCustomerView: Fetching bills with params:', params);
      console.log('BillCustomerView: Date range:', {
        selectedMonth,
        start: getDateRange.start,
        end: getDateRange.end,
        siteId: externalSite ? resolveSiteValue(externalSite) : null,
      });

      // Add search filters
      if (orderIdFilter.trim()) {
        params.order_id_contains = orderIdFilter.trim();
        params.bill_number_contains = orderIdFilter.trim();
      }

      // Use last_key for pagination if not resetting
      if (!reset && currentLastKey) {
        params.last_key = typeof currentLastKey === 'string' ? currentLastKey : JSON.stringify(currentLastKey);
      }

      const result = await billApi.listBills(params);

      if (result.success) {
        const mappedBills = (result.bills || []).map(mapBillToUI);
        
        if (reset) {
          setBills(mappedBills);
        } else {
          setBills(prev => [...prev, ...mappedBills]);
        }
        
        setLastKey(result.last_key);
        setHasMore(result.has_more || false);
      } else {
        // Handle specific error status codes
        if (result.status === 502) {
          throw new Error('Backend service temporarily unavailable. Please try again in a moment.');
        } else if (result.status === 503) {
          throw new Error('Service temporarily unavailable. Please try again later.');
        } else {
          throw new Error(result.error || 'Failed to fetch bills');
        }
      }
    } catch (err) {
      console.error('Error fetching bills:', err);
      // Provide user-friendly error messages
      let errorMessage = 'Unable to load bills. Please try again later.';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.status === 502) {
        errorMessage = 'Backend service temporarily unavailable. The server may be experiencing issues. Please try again in a moment.';
      } else if (err.status === 503) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      }
      setError(errorMessage);
      if (reset) {
        setBills([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [getDateRange, orderIdFilter, externalSite, fetchFromFdoc, esignedOnly]);

  // Fetch bills when month changes
  useEffect(() => {
    setLastKey(null);
    setHasMore(false);
    fetchBills(true, null);
  }, [selectedMonth, fetchBills]);

  // Debounce search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      setLastKey(null);
      setHasMore(false);
      fetchBills(true, null);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [orderIdFilter, fetchBills]);

  // Fetch full bill details when card is clicked
  const handleCardClick = async (bill) => {
    setSelectedCard(bill);
    setDownloadUrl(null);
    setDownloadUrlExpiresIn(null);
    setDocumentContentType(null);
    setBillFileStatus(null);
    setFullBillDetails(null);

    // Check if this is an FDOC document (OO bill)
    if (bill.fdoc_id || bill.apiBill?.fdoc_id) {
      const fdocId = bill.fdoc_id || bill.apiBill?.fdoc_id;
      setIsLoadingDetails(true);
      try {
        const fullDoc = await fdocApi.getDocument(fdocId);
        console.log('FDOC Document fetched:', fullDoc);

        // Try to find a direct download URL first
        const downloadUrlFromDoc = fullDoc?.download_url || fullDoc?.file_url || fullDoc?.s3_url || fullDoc?.presigned_url || fullDoc?.view_url;
        if (downloadUrlFromDoc && (downloadUrlFromDoc.startsWith('http://') || downloadUrlFromDoc.startsWith('https://'))) {
          setDownloadUrl(downloadUrlFromDoc);
          setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
        } else {
          // Get S3 key from document - try keys in priority order
          // Build list of available keys in priority order
          const availableKeys = [
            fullDoc?.s3_key_permanent,
            fullDoc?.s3_key,
            fullDoc?.s3_key_temp,
            fullDoc?.object_key
          ].filter(key => key && key.trim() !== '');
          
          console.log('BillCustomerView: Available S3 keys:', {
            s3_key_permanent: fullDoc?.s3_key_permanent,
            s3_key: fullDoc?.s3_key,
            s3_key_temp: fullDoc?.s3_key_temp,
            object_key: fullDoc?.object_key,
            availableKeys
          });
          
          if (availableKeys.length === 0) {
            console.warn('BillCustomerView: No S3 key or download URL found in FDOC document');
            return;
          }
          
          // Try each key in order until one works
          const bucket = fullDoc?.bucket || 'innoworx-fdoc-permanent';
          let presignedUrl = null;
          
          for (const s3Key of availableKeys) {
            try {
              // Check if it's already a URL
              if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
                console.log('BillCustomerView: Using direct URL:', s3Key);
                setDownloadUrl(s3Key);
                setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
                return;
              }
              
              // Convert S3 key to S3 path format
              const s3Path = `s3://${bucket}/${s3Key}`;
              console.log('BillCustomerView: Trying S3 path:', s3Path);
              
              // Get presigned URL from S3 path
              presignedUrl = await userService.getS3PresignedUrl(s3Path);
              if (presignedUrl) {
                // Check if URL has wrong region (this will cause 400/403 errors)
                if (presignedUrl.includes('ap-south-1')) {
                  console.error(
                    '❌ CRITICAL: Presigned URL region mismatch.\n' +
                    'The S3 bucket (innoworx-fdoc-permanent) is in us-east-1, but the presigned URL is generated with ap-south-1.\n' +
                    'This will cause 400/403 errors. Backend fix required: The /s3-presigned endpoint must detect the bucket region and generate URLs with us-east-1.'
                  );
                }
                console.log('BillCustomerView: Successfully got presigned URL for key:', s3Key);
                setDownloadUrl(presignedUrl);
                setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || fullDoc?.file_type ? `application/${fullDoc.file_type}` : 'application/pdf');
                return; // Success, exit loop
              }
            } catch (error) {
              console.warn(`BillCustomerView: Failed to get presigned URL for key "${s3Key}":`, error);
              // Continue to next key
              continue;
            }
          }
          
          // If we get here, all keys failed
          console.error('BillCustomerView: All S3 keys failed to generate presigned URLs');
        }
        
        // Do NOT set fullBillDetails for FDOC bills to hide the preview
      } catch (err) {
        console.error('Error fetching FDOC document:', err);
      } finally {
        setIsLoadingDetails(false);
      }
      return;
    }

    // If we already have full details with line items, use them
    if (bill.apiBill?.line_items && bill.apiBill.line_items.length > 0) {
      setFullBillDetails(bill.apiBill);
      return;
    }

    // Otherwise fetch full details from bill API (only if not from FDOC)
    // For FDOC documents, we should have fdoc_id, so if we reach here and fetchFromFdoc is true,
    // it means the document doesn't have fdoc_id set, so use the existing data
    if (fetchFromFdocRef.current) {
      // For FDOC documents, use the existing apiBill data
      setFullBillDetails(bill.apiBill || bill);
      return;
    }

    // Only fetch from bill API if this is not an FDOC document
    if (bill.billId && !bill.fdoc_id) {
      setIsLoadingDetails(true);
      try {
        // Fetch bill with download URL for document display
        const result = await billApi.getBill(bill.billId, { include_download_url: true });
        if (result.success && result.bill) {
          setFullBillDetails(result.bill);
          
          // Set document URL and metadata if available
          if (result.download_url && result.bill.bill_file_status === 'stored') {
            setDownloadUrl(result.download_url);
            setDocumentContentType(result.bill.bill_file_content_type || 'application/pdf');
            setDownloadUrlExpiresIn(result.download_url_expires_in || 900);
            setBillFileStatus(result.bill.bill_file_status);
          } else {
            // Reset document states if no document available
            setDownloadUrl(null);
            setDocumentContentType(null);
            setDownloadUrlExpiresIn(null);
            setBillFileStatus(result.bill.bill_file_status || 'not_provided');
          }
        } else {
          // Fallback to summary data
          setFullBillDetails(bill.apiBill);
          setDownloadUrl(null);
          setDocumentContentType(null);
          setBillFileStatus(null);
        }
      } catch (err) {
        console.error('Error fetching bill details:', err);
        // Fallback to summary data
        setFullBillDetails(bill.apiBill);
        setDownloadUrl(null);
        setDocumentContentType(null);
        setBillFileStatus(null);
      } finally {
        setIsLoadingDetails(false);
      }
    } else {
      setFullBillDetails(bill.apiBill || bill);
      setDownloadUrl(null);
      setDocumentContentType(null);
      setBillFileStatus(null);
    }
  };

  // Map full bill details to UI format for detail modal
  const getFullBillForModal = useMemo(() => {
    if (!fullBillDetails) return selectedCard;

    const billDate = fullBillDetails.bill_timestamp 
      ? new Date(fullBillDetails.bill_timestamp).toISOString().split('T')[0]
      : fullBillDetails.created_at 
        ? new Date(fullBillDetails.created_at).toISOString().split('T')[0]
        : selectedCard?.date || '';

    return {
      ...selectedCard,
      orderNumber: fullBillDetails.order_id || selectedCard?.orderNumber || '',
      billNumber: fullBillDetails.bill_number || selectedCard?.billNumber || '',
      date: billDate,
      tableNumber: fullBillDetails.table_number || selectedCard?.tableNumber || '',
      siteName: fullBillDetails.site_name || selectedCard?.siteName || '',
      customerName: fullBillDetails.customer?.name || selectedCard?.customerName || '',
      customerMobile: fullBillDetails.customer?.mobile || selectedCard?.customerMobile || '',
      customerEmail: fullBillDetails.customer?.email || selectedCard?.customerEmail || '',
      items: (fullBillDetails.line_items || []).map(item => ({
        item: item.name || '',
        quantity: item.quantity || 0,
        pricePerUnit: item.price_per_unit || 0,
        subtotal: item.subtotal || (item.quantity * item.price_per_unit),
      })),
      subtotal: fullBillDetails.subtotal || 0,
      discountCode: fullBillDetails.discount_code || '',
      discountValue: fullBillDetails.discount_amount || 0,
      discountedSubtotal: (fullBillDetails.subtotal || 0) - (fullBillDetails.discount_amount || 0),
      cgstRate: fullBillDetails.cgst && fullBillDetails.subtotal 
        ? ((fullBillDetails.cgst / (fullBillDetails.subtotal - (fullBillDetails.discount_amount || 0))) * 100).toFixed(1) 
        : 0,
      sgstRate: fullBillDetails.sgst && fullBillDetails.subtotal 
        ? ((fullBillDetails.sgst / (fullBillDetails.subtotal - (fullBillDetails.discount_amount || 0))) * 100).toFixed(1) 
        : 0,
      cgstAmount: fullBillDetails.cgst || 0,
      sgstAmount: fullBillDetails.sgst || 0,
      grandTotal: fullBillDetails.total || 0,
    };
  }, [fullBillDetails, selectedCard]);

  // Function to refresh download URL when it expires
  const refreshDownloadUrl = useCallback(async () => {
    if (!selectedCard?.billId && !fullBillDetails?.bill_id) return;
    
    const billId = selectedCard?.billId || fullBillDetails?.bill_id;
    try {
      const result = await billApi.getBill(billId, { include_download_url: true });
      if (result.success && result.download_url && result.bill.bill_file_status === 'stored') {
        setDownloadUrl(result.download_url);
        setDocumentContentType(result.bill.bill_file_content_type || 'application/pdf');
        setDownloadUrlExpiresIn(result.download_url_expires_in || 900);
        return result.download_url;
      }
    } catch (err) {
      console.error('Error refreshing download URL:', err);
    }
    return null;
  }, [selectedCard, fullBillDetails]);

  const handleDownload = (bill) => {
    if (!bill) return;

    try {
      // Create A4 PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      // Helper function to center text
      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to add left-aligned text
      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to add two-column text (left and right)
      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      // Helper function to draw line
      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('BILL', 18, true);
      yPosition += 3;
      if (bill.siteName) {
        centerText(bill.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // Bill Information
      addText('BILL INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('Bill Number:', bill.billNumber || bill.orderNumber || 'N/A', 10);
      if (bill.orderNumber) {
        addTwoColumn('Order Number:', bill.orderNumber, 10);
      }
      addTwoColumn('Date:', bill.date || 'N/A', 10);
      if (bill.tableNumber) {
        addTwoColumn('Table Number:', bill.tableNumber, 10);
      }
      if (bill.siteName) {
        addTwoColumn('Site:', bill.siteName, 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Customer Information
      if (bill.customerName) {
        addText('CUSTOMER INFORMATION', 12, true);
        yPosition += 2;
        addTwoColumn('Name:', bill.customerName, 10);
        if (bill.customerMobile) {
          addTwoColumn('Mobile:', bill.customerMobile, 10);
        }
        if (bill.customerEmail) {
          addTwoColumn('Email:', bill.customerEmail, 10);
        }
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('LINE ITEMS', 12, true);
      yPosition += 3;

      // Items Table Header
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Item Name', margin + 20, yPosition);
      pdf.text('Qty', margin + 100, yPosition);
      pdf.text('Rate', margin + 120, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (bill.items && bill.items.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        bill.items.forEach((item, index) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.item || item.name || `Item ${index + 1}`;
          const quantity = item.quantity || 0;
          const rate = item.pricePerUnit || item.rate || 0;
          const amount = item.subtotal || (quantity * rate);

          // Wrap item name if too long
          const itemLines = pdf.splitTextToSize(itemName, 70);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text(quantity.toString(), margin + 100, yPosition);
          pdf.text(formatCurrency(rate), margin + 120, yPosition);
          pdf.text(formatCurrency(amount), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Check if we need a new page for summary
      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary Section
      addText('SUMMARY', 12, true);
      yPosition += 2;

      if (bill.subtotal !== undefined) {
        addTwoColumn('Subtotal:', formatCurrency(bill.subtotal), 10);
      }

      if (bill.discountValue > 0) {
        addTwoColumn(`Discount (${bill.discountCode || 'N/A'}):`, `-${formatCurrency(bill.discountValue)}`, 10);
      }

      if (bill.cgstAmount > 0) {
        addTwoColumn(`CGST (${bill.cgstRate || 0}%):`, formatCurrency(bill.cgstAmount), 10);
      }

      if (bill.sgstAmount > 0) {
        addTwoColumn(`SGST (${bill.sgstRate || 0}%):`, formatCurrency(bill.sgstAmount), 10);
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Grand Total
      addTwoColumn('GRAND TOTAL:', formatCurrency(bill.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      // Footer
      centerText('Thank you for your business!', 10);
      yPosition += 2;

      // Save PDF
      const fileName = `bill-${bill.orderNumber || bill.billNumber || 'unknown'}-${bill.date || 'unknown'}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Failed to generate bill PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const formatCurrency = (amount) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <div className="rounded-3xl">
            {/* Order ID Filter - Always visible, Month Selection - Only show if not hidden */}
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
              <div className="min-w-[200px] flex-1">
                <label htmlFor="bill-customer-order-id" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Search className="w-4 h-4" />
                  Filter by Order ID
                </label>
                <input
                  id="bill-customer-order-id"
                  type="text"
                  placeholder="Enter Order ID or Bill Number..."
                  value={orderIdFilter}
                  onChange={(event) => setOrderIdFilter(event.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              {!hideSelectors && (
                <div className="min-w-[200px]">
                  <label htmlFor="bill-customer-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <Calendar className="w-4 h-4" />
                    Select Month
                  </label>
                  <input
                    id="bill-customer-month"
                    type="month"
                    value={selectedMonth}
                    onChange={(event) => setSelectedMonth(event.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>
              )}
            </div>

            {/* Loading State */}
            {isLoading && bills.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading bills...</p>
              </div>
            )}

            {/* Error State */}
            {error && bills.length === 0 && (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={() => fetchBills(true)}
                  className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Bills Table */}
            <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full border-collapse text-sm md:text-base">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">S.No</th>
                    <th className="px-4 py-3 font-semibold min-w-[180px]">Order#</th>
                    <th className="px-4 py-3 font-semibold min-w-[180px]">Bill#</th>
                    <th className="px-4 py-3 font-semibold min-w-[140px]">Date</th>
                    <th className="px-4 py-3 font-semibold min-w-[140px]">Table</th>
                    <th className="px-4 py-3 font-semibold min-w-[220px]">Customer</th>
                    <th className="px-4 py-3 font-semibold min-w-[180px]">Site</th>
                    <th className="px-4 py-3 font-semibold min-w-[100px]">Version</th>
                    <th className="px-4 py-3 font-semibold min-w-[160px]">Grand Total</th>
                    <th className="px-4 py-3 font-semibold min-w-[200px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill, index) => {
                    const rowVersion = bill.version_no ?? bill.versionNo;
                    return (
                    <tr
                      key={bill.billId || bill.id || `bill-row-${index}`}
                      className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                      onClick={() => handleCardClick(bill)}
                    >
                      <td className="px-4 py-4 align-top text-gray-800">{index + 1}</td>
                      <td className="px-4 py-4 align-top text-gray-800">{bill.orderNumber || '—'}</td>
                      <td className="px-4 py-4 align-top text-gray-700">{bill.billNumber || '—'}</td>
                      <td className="px-4 py-4 align-top text-gray-700">{bill.date || '—'}</td>
                      <td className="px-4 py-4 align-top text-gray-700">{bill.tableNumber || '—'}</td>
                      <td className="px-4 py-4 align-top text-gray-700">
                        <div className="max-w-[320px] truncate" title={bill.customerName || ''}>
                          {bill.customerName || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-700">
                        <div className="max-w-[260px] truncate" title={bill.siteName || ''}>
                          {bill.siteName || '—'}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-top text-gray-800">
                        {rowVersion != null && rowVersion !== '' ? `v${rowVersion}` : '—'}
                      </td>
                      <td className="px-4 py-4 align-top text-gray-800">{formatCurrency(bill.grandTotal)}</td>
                      <td className="px-4 py-4 align-top">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(bill);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800"
                            title="Download"
                          >
                            <Download className="h-4 w-4 text-white stroke-white" strokeWidth={2} />
                            Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>

            {!isLoading && bills.length === 0 && !error && (
              <div className="text-center py-12">
                <p className="text-gray-500">No bills found matching the selected criteria.</p>
              </div>
            )}

            {/* Load More Button */}
            {hasMore && !isLoading && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => fetchBills(false, lastKey)}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Load More
                </button>
              </div>
            )}

            {/* Loading More Indicator */}
            {isLoading && bills.length > 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">Loading more bills...</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Detail Modal */}
      {selectedCard &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
              <div className="flex-shrink-0 px-4 pt-6 pb-4">
                <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                    {selectedCard?.fdoc_id || selectedCard?.apiBill?.fdoc_id ? 'Document' : 'Bill Details'}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCard(null);
                        setFullBillDetails(null);
                        setDownloadUrl(null);
                        setDownloadUrlExpiresIn(null);
                        setDocumentContentType(null);
                        setBillFileStatus(null);
                      }}
                      className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                    >
                      Close
                    </button>
                    {!(selectedCard?.fdoc_id || selectedCard?.apiBill?.fdoc_id) && (
                      <button
                        type="button"
                        onClick={() => handleDownload(getFullBillForModal || selectedCard)}
                        className="rounded-full bg-emerald-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                          Download PDF
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
                >
                  {isLoadingDetails ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Loading...</p>
                    </div>
                  ) : (
                    <>
                      {/* For OO (FDOC) bills: Only show PDF document, no preview */}
                      {(selectedCard?.fdoc_id || selectedCard?.apiBill?.fdoc_id) ? (
                        <>
                          {downloadUrl ? (
                            <div className="w-full h-full min-h-[80vh]">
                              {documentContentType && documentContentType.startsWith('image/') ? (
                                // Display images using img tag
                                <div className="flex justify-center items-center w-full h-full min-h-[80vh] p-4">
                                  <img
                                    src={downloadUrl}
                                    alt="Bill Document"
                                    className="max-w-full max-h-full object-contain rounded-lg"
                                  />
                                </div>
                              ) : (
                                // Display PDFs using iframe - full screen
                                <div className="w-full h-full min-h-[80vh]">
                                  <iframe
                                    src={downloadUrl}
                                    title="Bill Document"
                                    className="w-full h-full min-h-[80vh] border-0"
                                    onError={() => {
                                      console.error('Failed to load PDF document. URL may be invalid or expired.');
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <div className="max-w-md mx-auto">
                                <p className="text-gray-500 mb-2">No document available or failed to load.</p>
                                {downloadUrl && (
                                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                                      Document URL Error
                                    </p>
                                    <p className="text-xs text-yellow-700">
                                      The document URL could not be loaded. This is likely due to a backend configuration issue where presigned URLs are generated with the wrong AWS region (ap-south-1 instead of us-east-1).
                                    </p>
                                    <p className="text-xs text-yellow-700 mt-2">
                                      Please contact support or check backend logs for region mismatch errors.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* For non-OO bills: Show bill details, no document viewer */}
                          <div className="flex items-start justify-between mb-6">
                            <div>
                              <h2 className="text-2xl font-semibold text-gray-900">{getFullBillForModal?.orderNumber || selectedCard?.orderNumber}</h2>
                              <p className="text-gray-500 mt-1">{getFullBillForModal?.billNumber || selectedCard?.billNumber}</p>
                              <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                                <Calendar className="w-4 h-4" />
                                <span className="font-medium">{getFullBillForModal?.date || selectedCard?.date}</span>
                              </div>
                            </div>
                          </div>

                          <div className="grid gap-6 md:grid-cols-2 mb-6">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Information</h3>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Table Number:</span>
                                  <span className="font-medium text-gray-700">{getFullBillForModal?.tableNumber || selectedCard?.tableNumber || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Date:</span>
                                  <span className="font-medium text-gray-700">{getFullBillForModal?.date || selectedCard?.date}</span>
                                </div>
                              </div>
                            </div>

                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Details</h3>
                              <div className="space-y-2 text-sm text-gray-700">
                                <p><span className="font-semibold text-gray-600">Name:</span> {getFullBillForModal?.customerName || selectedCard?.customerName || 'N/A'}</p>
                                <p><span className="font-semibold text-gray-600">Mobile:</span> {getFullBillForModal?.customerMobile || selectedCard?.customerMobile || 'N/A'}</p>
                                <p><span className="font-semibold text-gray-600">Email:</span> {getFullBillForModal?.customerEmail || selectedCard?.customerEmail || 'N/A'}</p>
                              </div>
                            </div>
                          </div>

                          <div className="mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Items</h3>
                    <div className="overflow-x-auto rounded-2xl border border-gray-200">
                      <table className="min-w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-left text-gray-600">
                            <th className="px-4 py-3 font-semibold">S.No</th>
                            <th className="px-4 py-3 font-semibold">Item</th>
                            <th className="px-4 py-3 font-semibold">Quantity</th>
                            <th className="px-4 py-3 font-semibold">Price per Unit</th>
                            <th className="px-4 py-3 font-semibold text-right">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(getFullBillForModal?.items || selectedCard?.items || []).map((item, index) => (
                            <tr key={index} className="border-t border-gray-100">
                              <td className="px-4 py-3">{index + 1}</td>
                              <td className="px-4 py-3">{item.item || item.name || 'N/A'}</td>
                              <td className="px-4 py-3">{item.quantity || 0}</td>
                              <td className="px-4 py-3">{formatCurrency(item.pricePerUnit || item.price_per_unit || 0)}</td>
                              <td className="px-4 py-3 text-right">{formatCurrency(item.subtotal || 0)}</td>
                            </tr>
                          ))}
                          {(!getFullBillForModal?.items || getFullBillForModal.items.length === 0) && (!selectedCard?.items || selectedCard.items.length === 0) && (
                            <tr>
                              <td colSpan={5} className="px-4 py-3 text-center text-gray-500">
                                {isLoadingDetails ? 'Loading items...' : 'No items found'}
                              </td>
                            </tr>
                          )}
                          <tr className="border-t border-gray-200 bg-gray-50 text-gray-700">
                            <td colSpan={4} className="px-4 py-3 font-semibold">Subtotal</td>
                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(getFullBillForModal?.subtotal || selectedCard?.subtotal || 0)}</td>
                          </tr>
                          {(getFullBillForModal?.discountValue || selectedCard?.discountValue || 0) > 0 && (
                            <tr className="border-t border-gray-100 bg-white text-gray-700">
                              <td colSpan={4} className="px-4 py-3 font-semibold">
                                Discount {(getFullBillForModal?.discountCode || selectedCard?.discountCode) ? `(${getFullBillForModal?.discountCode || selectedCard?.discountCode})` : ''}
                              </td>
                              <td className="px-4 py-3 text-right font-semibold text-green-600">
                                - {formatCurrency(getFullBillForModal?.discountValue || selectedCard?.discountValue || 0)}
                              </td>
                            </tr>
                          )}
                          <tr className="border-t border-gray-100 bg-white text-gray-700">
                            <td colSpan={4} className="px-4 py-3 font-semibold">Subtotal (After Discount)</td>
                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(getFullBillForModal?.discountedSubtotal || selectedCard?.discountedSubtotal || 0)}</td>
                          </tr>
                          <tr className="border-t border-gray-100 bg-white text-gray-700">
                            <td colSpan={4} className="px-4 py-3 font-semibold">CGST ({getFullBillForModal?.cgstRate || selectedCard?.cgstRate || 0}%)</td>
                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(getFullBillForModal?.cgstAmount || selectedCard?.cgstAmount || 0)}</td>
                          </tr>
                          <tr className="border-t border-gray-100 bg-white text-gray-700">
                            <td colSpan={4} className="px-4 py-3 font-semibold">SGST ({getFullBillForModal?.sgstRate || selectedCard?.sgstRate || 0}%)</td>
                            <td className="px-4 py-3 text-right font-semibold">{formatCurrency(getFullBillForModal?.sgstAmount || selectedCard?.sgstAmount || 0)}</td>
                          </tr>
                          <tr className="border-t border-gray-200 bg-gray-100 text-gray-900 font-semibold">
                            <td colSpan={4} className="px-4 py-3">Grand Total</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(getFullBillForModal?.grandTotal || selectedCard?.grandTotal || 0)}</td>
                          </tr>
                        </tbody>
                      </table>
                            </div>
                          </div>

                          {/* Bill Document Section for non-FDOC bills */}
                          {billFileStatus === 'stored' && downloadUrl && (
                            <div className="mt-6">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Document</h3>
                              {documentContentType && documentContentType.startsWith('image/') ? (
                                // Display images
                                <div className="flex justify-center items-center w-full p-4 bg-gray-50 rounded-lg">
                                  <img
                                    src={downloadUrl}
                                    alt={`Bill ${getFullBillForModal?.billNumber || selectedCard?.billNumber}`}
                                    className="max-w-full max-h-[600px] object-contain rounded-lg"
                                    onError={async () => {
                                      // Handle expired URL - regenerate by calling API again
                                      console.error('Image failed to load, URL may have expired. Regenerating...');
                                      await refreshDownloadUrl();
                                    }}
                                  />
                                </div>
                              ) : documentContentType === 'application/pdf' ? (
                                // Display PDFs
                                <div className="w-full">
                                  <iframe
                                    src={downloadUrl}
                                    title={`Bill ${getFullBillForModal?.billNumber || selectedCard?.billNumber}`}
                                    className="w-full h-[600px] border border-gray-200 rounded-lg"
                                    onError={async () => {
                                      // Handle expired URL - regenerate by calling API again
                                      console.error('PDF failed to load, URL may have expired. Regenerating...');
                                      await refreshDownloadUrl();
                                    }}
                                  />
                                  <div className="mt-2">
                                    <a
                                      href={downloadUrl}
                                      download={`${getFullBillForModal?.billNumber || selectedCard?.billNumber || 'bill'}.pdf`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
                                    >
                                      <Download className="w-4 h-4" />
                                      Download PDF
                                    </a>
                                  </div>
                                </div>
                              ) : (
                                // Other document types - show download link
                                <div className="p-4 bg-gray-50 rounded-lg">
                                  <a
                                    href={downloadUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
                                  >
                                    <Download className="w-4 h-4" />
                                    View Document
                                  </a>
                                </div>
                              )}
                            </div>
                          )}

                          {billFileStatus === 'pending_upload' && (
                            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">Document upload pending</p>
                            </div>
                          )}

                          {billFileStatus === 'not_provided' && (
                            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                              <p className="text-sm text-gray-600">No document available</p>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default BillCustomerView;

